import { FeatureState, isFeatureEnabled } from '@etrigan/feature-toggles-client'
import {
    AllEvents,
    blockBreachEvent,
    ConsentLevel,
    ConsentState,
    DataLayerEventName,
    Features,
    GetDcrMeta,
    getDefaultConsentLevel,
    handleChartbeatTracking,
    hasConsentLevel,
    HttpError,
    isBreachEvent,
    LocationState,
    setBreachState,
} from '@news-mono/web-common'
import { PageLifecycleProviderRenderProps } from 'page-lifecycle-provider'
import queryString from 'query-string'
import { DataProviderEvents } from 'react-ssr-data-loader'
import { Logger } from 'typescript-log'
import { purgeTruskin } from '../advertising/ad-helpers/truskin'
import {
    handlePageEventsInDatalayer,
    toDataLayerEvent,
} from '../tracking/gtm/gtm'
import { nielsenSoftPageLoad } from '../tracking/nielsen/nielsen'

let externalEventsDisabled = false

declare const _satellite: undefined | { track(url: string): void }

export const disableExternalEvents = () => (externalEventsDisabled = true)

export function handleDataProviderEvent(
    pageProps: PageLifecycleProviderRenderProps,
    // We want to pass the logger in here as it could
    logger: Logger,
): (event: DataProviderEvents) => void {
    return (event) => {
        if (event.type === 'state-changed') {
            return
        }

        const { data } = event
        const dataResourceInfo = `${data.resourceType} - ${data.resourceLoadParamsHash}`

        if (event.type === 'begin-loading-event') {
            logger.debug(
                `client - Signal start loading data: ${dataResourceInfo}`,
            )
            pageProps.beginLoadingData()
        } else if (event.type === 'end-loading-event') {
            logger.debug(
                `client - Signal end loading data: ${dataResourceInfo}`,
            )
            pageProps.endLoadingData()
        } else if (event.type === 'load-error') {
            if (event.data.error.message === 'failed-data-load') {
                return
            }

            logger.debug(
                `client - Signal data load failed: ${dataResourceInfo}`,
            )
            if (
                event.data.error instanceof HttpError ||
                event.data.error.message === '404'
            ) {
                return
            }

            logger.error(
                { err: event.data.error },
                'client - Data load failed with unknown error',
            )
        }
    }
}

/** Ensures Bonzai units are cleared on loading a new page as Bonzai ad units don't do this themselves. */
const handleBonzaiAds = (event: AllEvents): void => {
    if (event.type !== 'page-load-started') {
        return
    }

    const bonzaiAds = document.querySelectorAll('iframe[adcontainer]')

    for (let index = 0; index < bonzaiAds.length; ++index) {
        const bonzaiNode = bonzaiAds[index]
        if (!bonzaiNode) {
            continue
        }
        const bonzaiParent = bonzaiNode.parentNode
        if (!bonzaiParent) {
            continue
        }

        bonzaiParent.removeChild(bonzaiNode)
    }
}

const handleTruskinAds = (event: AllEvents): void => {
    if (event.type === 'page-load-started') {
        purgeTruskin()
    }
}

const dataLayerEventHandler = handlePageEventsInDatalayer<AllEvents>(
    (event: AllEvents, actualPageVariables: any) => {
        if (
            event.type === 'route-info-resolver.failed' ||
            event.type === 'page-render.failed'
        ) {
            return
        }
        return toDataLayerEvent(event, actualPageVariables)
    },
)

export interface HandlePageEventProps {
    event: AllEvents
    log: Logger
    toggleState: FeatureState
    actualPageVariables: any
    nielsenSiteName: string
    getDcrMeta: GetDcrMeta
    consentState: ConsentState
    publicHostname: string
    chartbeatId?: number
    locationState: LocationState
}

export function handlePageEvents({
    event,
    log,
    toggleState,
    actualPageVariables,
    nielsenSiteName,
    getDcrMeta,
    consentState,
    publicHostname,
    chartbeatId,
    locationState,
}: HandlePageEventProps) {
    const nielsenEnabled =
        isFeatureEnabled(toggleState, 'nielsen-events') &&
        hasConsentLevel(
            consentState.consentLevel ?? ConsentLevel.Essential,
            ConsentLevel.Analytics,
        )

    const adobeAudienceManagerEnabled =
        isFeatureEnabled<Features>(toggleState, 'adobe-audience-manager') &&
        hasConsentLevel(
            consentState.consentLevel ?? ConsentLevel.Essential,
            ConsentLevel.Analytics,
        )

    const activeConsentLevel =
        consentState.consentLevel || getDefaultConsentLevel(locationState)
    const chartbeatEnabled =
        chartbeatId &&
        isFeatureEnabled(toggleState, 'native-chartbeat') &&
        activeConsentLevel >= 2

    log.debug(event, 'Page event raised')

    if (isBreachEvent(event)) {
        if (blockBreachEvent(event)) {
            log.debug(event, 'Block breach event')
            return
        }
        log.debug(event, 'Updating breach state')
        setBreachState(event)
    }

    dataLayerEventHandler(event, log, actualPageVariables)
    handleBonzaiAds(event)
    handleTruskinAds(event)

    if (chartbeatEnabled) {
        handleChartbeatTracking(event, chartbeatId!, publicHostname)
    }

    if (
        event.type === DataLayerEventName.breachScreenComplete ||
        event.type === DataLayerEventName.subscribeComplete
    ) {
        // DPT-942: Prevent multiple breachscreen.complete events triggering
        try {
            if (typeof window !== 'undefined') {
                const {
                    productName,
                    productSku,
                    transactionTax,
                    transactionTotal,
                    clickOrigin,
                    ...search
                } = queryString.parse(location.search)

                // Remove the ga query string added if a regwall event happened
                if (search) {
                    const searchQueryItems = Object.keys(search)
                    let gaKey: string | null = null
                    for (const key of searchQueryItems) {
                        if (key.match(/ga\d+.\d+.\d+.\d+/)) {
                            gaKey = key as string
                        }
                    }

                    // Remove the GA query param e.g ga1.1.2066668464.1598930365
                    if (gaKey) {
                        delete search[gaKey]
                    }
                }

                // We want to retain search params except all the breach screen params except transactionId, which
                // is needed by gtm, by not having the clickOrigin present the breachscreen.complete and subscribe.complete won't fire.
                const searchExcludingBreachParams =
                    queryString.stringify(search)

                // Only add the search params on if there are some
                const url = `${window.location.pathname}${
                    window.location.hash
                }${
                    searchExcludingBreachParams.length > 0
                        ? `?${searchExcludingBreachParams}`
                        : ''
                }`

                window.history.replaceState({}, document.title, url)
            }
        } catch (err) {
            // not much we can do
        }
    }

    if (event.type === 'page-load-complete' && !externalEventsDisabled) {
        if (nielsenEnabled) {
            nielsenSoftPageLoad(log, getDcrMeta, nielsenSiteName)
        }
        if (
            adobeAudienceManagerEnabled &&
            typeof _satellite !== 'undefined' &&
            typeof _satellite.track !== 'undefined'
        ) {
            _satellite.track('spa_link')
        }
    }
}
